import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Confirm, Icons, Modal } from '@components/common';
import PageTitle from '@components/common/PageTitle';
import Table from '@components/common/Table';
import ManageSiteForm from '@components/ModalComponents/ManageSiteForm';
import { getColumns } from '@components/routes/AdminPanel/ManageCrowdCells/manageCrowdCellsColumns';

import { useMutateCreateSiteAndLink } from '@hooks/cells/useMutateCreateSiteAndLink';
import { useMutateLinkCrowdCell } from '@hooks/crowdCells/useMutateLinkCrowdCell';
import { useMutateUnlinkCrowdCell } from '@hooks/crowdCells/useMutateUnlinkCrowdCell';
import { useQueryCrowdCells } from '@hooks/crowdCells/useQueryCrowdCells';
import { useMutateGenerateSiteId } from '@hooks/sites/useMutateGenerateSiteId';
import { TGenerateSiteId } from '@hooks/useManageSites';

import { crowdCells as crowdCellsSelector } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import {
	setIms,
	setIsLinked,
	setMme,
	setPage,
	setPageSize,
	setSearchField,
	setSearchValue,
	setSort,
	setStatus,
} from '@slices/crowdCells';

import { setFilterUtil } from '@utils/filterUtil';

import { IManageSiteValuesDTO } from '@interfaces/cells/dto/cellsDTO';
import { TSelectOption } from '@interfaces/components/componentTypes';
import {
	ISortBy,
	ITablePagination,
	ITableSearch,
	ITableSort,
	TTableFilter,
} from '@interfaces/components/table';
import { TUnlinkData } from './types';

import { ECrowdCellFields, ESearchType } from './enums';

import { MODAL_WIDTH_455, MODAL_WIDTH_480, MODAL_WIDTH_580 } from '@constants/modals';
import {
	entityOptions,
	filterWidth,
	linkedOptions,
	linkTooltipContent,
	statusOptions,
	unlinkTooltipContent,
} from './constants';

import LinkSiteForm from './LinkSiteForm';

import styles from './ManageCrowdCells.module.scss';

const ManageCrowdCells = () => {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	const [unlinkData, setUnlinkData] = useState<TUnlinkData | null>(null);
	const [selectedCrowdCellId, setSelectedCrowdCell] = useState<string | null>(null);

	const [isDirtyFormModal, setIsDirtyFormModal] = useState(false);

	const [isShownAddSiteModal, setIsShownAddSiteModal] = useState<boolean>(false);
	const [isShownUnlinkModal, setIsShownUnlinkModal] = useState<boolean>(false);
	const [isShownLinkCrowdCell, setIsShownLinkModal] = useState<boolean>(false);

	const openAddSiteModal = () => setIsShownAddSiteModal((prevState) => !prevState);
	const openLinkCrowdCell = () => setIsShownLinkModal((prevState) => !prevState);
	const openUnlinkConfirm = () => setIsShownUnlinkModal((prevState) => !prevState);

	const closeUnlinkModal = () => {
		setIsShownUnlinkModal(false);
		setUnlinkData(null);
	};

	const closeLinkModal = () => {
		setIsShownLinkModal(false);
		setSelectedCrowdCell(null);
	};

	const closeSiteModal = () => {
		setIsShownAddSiteModal(false);
		setSelectedCrowdCell(null);
	};

	const linkActions = [
		{
			label: 'Link to site',
			name: 'exist',
			action: openLinkCrowdCell,
		},
		{
			label: 'Add new site and link to it',
			name: 'add',
			action: openAddSiteModal,
		},
	];

	const {
		page,
		pageSize,
		sortDirection,
		sortFieldName,
		searchValue,
		searchField,
		ims,
		mme,
		status,
		isLinked,
	} = useAppSelector(crowdCellsSelector);

	const [selectedMme, setSelectedMme] = useState<TSelectOption | null>(null);
	const [selectedIms, setSelectedIms] = useState<TSelectOption | null>(null);
	const [selectedStatus, setSelectedStatus] = useState<TSelectOption | null>(null);
	const [selectedIsLinked, setSelectedIsLinked] = useState<TSelectOption | null>(null);

	useEffect(() => {
		setFilterUtil({
			selectedValue: selectedMme,
			currentValue: mme,
			setAction: setMme,
			fieldName: ECrowdCellFields.mme,
			dispatch,
		});
	}, [dispatch, mme, selectedMme]);

	useEffect(() => {
		setFilterUtil({
			selectedValue: selectedIms,
			currentValue: ims,
			setAction: setIms,
			fieldName: ECrowdCellFields.ims,
			dispatch,
		});
	}, [dispatch, ims, selectedIms]);

	useEffect(() => {
		setFilterUtil({
			selectedValue: selectedStatus,
			currentValue: status,
			setAction: setStatus,
			fieldName: ECrowdCellFields.status,
			dispatch,
		});
	}, [dispatch, selectedStatus, status]);

	useEffect(() => {
		setFilterUtil({
			selectedValue: selectedIsLinked,
			currentValue: isLinked,
			setAction: setIsLinked,
			fieldName: ECrowdCellFields.isLinked,
			dispatch,
		});
	}, [dispatch, isLinked, selectedIsLinked]);

	useEffect(
		() => setSearch(() => defaultSearch),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[searchValue]);

	const filters: Array<TTableFilter> = [
		{
			name: ECrowdCellFields.isLinked,
			placeholder: 'Site ID',
			option: selectedIsLinked,
			options: linkedOptions,
			minWidth: filterWidth,
			setState: setSelectedIsLinked,
			defaultValue: isLinked,
		},
		{
			name: ECrowdCellFields.status,
			placeholder: 'Status',
			option: selectedStatus,
			options: statusOptions,
			minWidth: filterWidth,
			setState: setSelectedStatus,
			defaultValue: status,
		},
		{
			name: ECrowdCellFields.mme,
			placeholder: 'MME',
			option: selectedMme,
			options: entityOptions,
			minWidth: filterWidth,
			setState: setSelectedMme,
			defaultValue: mme,
		},
		{
			name: ECrowdCellFields.ims,
			placeholder: 'IMS',
			option: selectedIms,
			options: entityOptions,
			minWidth: filterWidth,
			setState: setSelectedIms,
			defaultValue: ims,
		},
	];

	const pageHandler = (page: number) => dispatch(setPage({ page }));
	const pageSizeHandler = (pageSize: number) => dispatch(setPageSize({ pageSize }));

	const onSearchSiteId = (searchValue: string) => {
		if (searchField !== ESearchType.siteId) dispatch(setSearchField({ searchField: ESearchType.siteId }));
		dispatch(setSearchValue({ searchValue }));
	};

	const onSearchNodeBId = (searchValue: string) => {
		if (searchField !== ESearchType.nodeBid) dispatch(setSearchField({ searchField: ESearchType.nodeBid }));
		dispatch(setSearchValue({ searchValue }));
	};

	const {
		crowdCells,
		countOfRecords,
		isLoadingCrowdCells,
	} = useQueryCrowdCells({
		page,
		pageSize,
		sortDirection,
		sortFieldName,
		searchValue,
		searchField,
		mme,
		ims,
		status,
		isLinked,
	});

	const { generateSiteId, isLoadingGenerateSiteId } = useMutateGenerateSiteId();
	const { handleUnlinkCrowdCell, isLoadingUnlinkCrowdCell } = useMutateUnlinkCrowdCell([closeUnlinkModal]);
	const { handleLinkCrowdCell, isLoadingLinkCrowdCell } = useMutateLinkCrowdCell([closeLinkModal]);
	const { handleAddSiteAndLink, isLoadingAddSiteAndLink } = useMutateCreateSiteAndLink([closeSiteModal]);

	const isLoadingAddForm = isLoadingAddSiteAndLink || isLoadingGenerateSiteId;
	const isLoading = (
		isLoadingCrowdCells ||
		isLoadingUnlinkCrowdCell ||
		isLoadingLinkCrowdCell ||
		isLoadingAddSiteAndLink
	);

	const pagination: ITablePagination = {
		countOfRecords,
		forcePage: page,
		size: pageSize,
		pageHandler: pageHandler,
		pageSizeHandler: pageSizeHandler,
	};

	const defaultSearch: Array<ITableSearch> = [
		{
			initialValue: searchValue,
			name: ESearchType.siteId,
			handler: onSearchSiteId,
			isNumber: true,
			tooltipText: 'Search by site ID',
			label: 'Site ID',
		},
		{
			initialValue: searchValue,
			name: ESearchType.nodeBid,
			isNumber: true,
			handler: onSearchNodeBId,
			tooltipText: 'Search by 4G (enb_ID), 5G (gnb_ID)',
			label: '4G (end_ID) or 5G (gnb_ID)',
		},
	];

	const [search, setSearch] = useState<Array<ITableSearch>>(() => defaultSearch);

	const addAndLinkHandler = (data: IManageSiteValuesDTO) =>
		handleAddSiteAndLink({ ...data, crowdCellId: selectedCrowdCellId } as any);

	const linkCrowdCellHandler = (siteDbId: string) =>
		handleLinkCrowdCell({ siteDbId, crowdCellId: selectedCrowdCellId } as any);

	const unlinkCrowdCellHandler = () => handleUnlinkCrowdCell(unlinkData as any);

	const handleSort = (sortBy: Array<ISortBy>) =>
		dispatch(
			setSort({
				sortDirection: !!sortBy[0]?.desc,
				sortFieldName: sortBy[0]?.id,
			}),
		);

	const sort: ITableSort = {
		initialState: [{ id: sortFieldName, desc: sortDirection }],
		handleSort,
	};

	const columns = getColumns({
		navigate,
		openUnlinkConfirm,
		setSelectedCrowdCell,
		setUnlinkData,
		linkActions,
		unlinkTooltipContent,
		linkTooltipContent,
	});

	return (
		<div className={styles.manageCrowdCellsContainer}>
			<PageTitle
				icon={Icons.crowdCellInverse()}
				title='Flex-5G Units'
			/>
			{isShownAddSiteModal && (
				<Modal
					isConfirmDialogNeededOnClose
					isDirtyForm={isDirtyFormModal}
					isOpen={isShownAddSiteModal}
					onClose={closeSiteModal}
					title='Add new site and link Flex-5G'
					width={MODAL_WIDTH_580}
				>
					<ManageSiteForm
						editableSite={null}
						generateSiteId={generateSiteId as unknown as TGenerateSiteId}
						isLinkMode
						isLoadingForm={isLoadingAddForm}
						setIsDirtyFormModal={setIsDirtyFormModal}
						submitHandler={addAndLinkHandler}
						toggleSiteModal={openAddSiteModal}
					/>
				</Modal>
			)}
			<Modal
				isOpen={isShownLinkCrowdCell}
				onClose={closeLinkModal}
				title='Link to site'
				width={MODAL_WIDTH_455}
			>
				<LinkSiteForm
					closeModal={closeLinkModal}
					submitHandler={linkCrowdCellHandler}
					toggleLinkModal={closeLinkModal}
				/>
			</Modal>
			<Modal
				isOpen={isShownUnlinkModal}
				onClose={closeUnlinkModal}
				title='Confirm action'
				width={MODAL_WIDTH_480}
			>
				<Confirm
					onClose={closeUnlinkModal}
					onSubmit={unlinkCrowdCellHandler}
					question='Are you sure you want to unlink this Flex-5G from the Site?'
				/>
			</Modal>
			<div className={styles.manageCrowdCellsListTableContainer}>
				<Table
					columns={columns}
					data={crowdCells}
					filters={filters}
					isLoading={isLoading}
					isOpenFilters={!!(mme || ims || status || isLinked)}
					isWithoutCheckbox
					isWithoutDeleteAction
					pagination={pagination}
					search={search}
					searchThreshold={0}
					sort={sort}
				/>
			</div>
		</div>
	);
};

export default ManageCrowdCells;
